/**
 * Super Admin — onglet "API & Intégrations".
 * Accès rapide à la doc Swagger, ReDoc, OpenAPI JSON, et page marketing /p/api.
 * Ce contenu était auparavant linké depuis le footer public — il est désormais
 * réservé au back-office Super Admin.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
    Code2, FileCode2, ExternalLink, Lock, Workflow, Globe2, Sparkles,
    BookOpen, Layers, ShieldCheck,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const DOCS_URL = `${API_BASE}/api/docs`;
const REDOC_URL = `${API_BASE}/api/redoc`;
const OPENAPI_URL = `${API_BASE}/api/openapi.json`;

const HIGHLIGHTS = [
    { icon: FileCode2, title: 'OpenAPI 3.1', desc: 'Endpoints documentés en temps réel via FastAPI. JSON pur, types stricts, codes HTTP standards.' },
    { icon: Lock, title: 'Auth Supabase JWT', desc: 'Tokens signés ES256, RLS multi-tenant, audit-trail immuable sur chaque mutation.' },
    { icon: Layers, title: 'Modulaire & versionné', desc: 'Routers spécialisés (auth, projects, meetings, super-admin, plans, campus, stripe, transcription…).' },
    { icon: Workflow, title: 'Intégrations métier', desc: 'GANTT, budget, GED, visio Jitsi, exports white-label PDF/XLSX/PPTX, IA Claude 3.5, Whisper, Stripe.' },
    { icon: Globe2, title: 'Souveraineté & RLS', desc: 'Isolation stricte par espace client. Vos données ne franchissent jamais la frontière de votre workspace.' },
    { icon: Sparkles, title: 'Webhooks (roadmap)', desc: 'Notifications sortantes (project.created, invoice.due, meeting.cr_emailed) — 2026.' },
];

function ActionCard({ icon: Icon, title, desc, href, internal, testid, accent = 'primary' }) {
    const accents = {
        primary: 'bg-primary-light text-primary',
        emerald: 'bg-emerald-50 text-emerald-700',
        violet: 'bg-violet-50 text-violet-700',
        amber: 'bg-amber-50 text-amber-700',
    };
    const content = (
        <div className="card-soft p-5 h-full hover:border-primary/30 hover:shadow-soft transition-all group">
            <div className="flex items-center justify-between mb-3">
                <div className={`h-11 w-11 rounded-xl ${accents[accent]} flex items-center justify-center`}>
                    <Icon size={18} strokeWidth={1.8} />
                </div>
                <ExternalLink size={14} className="text-foreground/30 group-hover:text-primary transition-colors" />
            </div>
            <h4 className="font-heading text-lg mb-1.5">{title}</h4>
            <p className="text-xs text-foreground/65 leading-relaxed">{desc}</p>
        </div>
    );
    if (internal) {
        return <Link to={href} data-testid={testid} className="block">{content}</Link>;
    }
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} className="block">
            {content}
        </a>
    );
}

export default function ApiIntegrationsAdmin() {
    return (
        <div className="space-y-8" data-testid="api-integrations-admin">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <p className="text-xs text-foreground/55 max-w-2xl">
                    Accès intégral à la documentation OpenAPI de la plateforme.
                    Ces ressources sont protégées Super Admin et ne sont plus accessibles
                    depuis le pied de page public.
                </p>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-medium">
                    <ShieldCheck size={12} /> Accès Super Admin uniquement
                </div>
            </div>

            {/* Quick links */}
            <div>
                <div className="text-[11px] uppercase tracking-wider text-foreground/45 font-semibold mb-3">
                    Accès direct
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <ActionCard
                        icon={BookOpen}
                        title="Swagger UI"
                        desc="Explorez et testez les 71+ endpoints en live. Auth via Bearer token Supabase."
                        href={DOCS_URL}
                        accent="primary"
                        testid="api-link-swagger"
                    />
                    <ActionCard
                        icon={FileCode2}
                        title="ReDoc"
                        desc="Documentation lisible (référence technique imprimable, idéale pour partenaires)."
                        href={REDOC_URL}
                        accent="violet"
                        testid="api-link-redoc"
                    />
                    <ActionCard
                        icon={Code2}
                        title="OpenAPI JSON"
                        desc="Spec brute 3.1 pour génération de SDK (Postman, openapi-generator, …)."
                        href={OPENAPI_URL}
                        accent="amber"
                        testid="api-link-openapi"
                    />
                    <ActionCard
                        icon={ExternalLink}
                        title="Page marketing /p/api"
                        desc="Page commerciale partenaires (bailleurs, ministères, ONG, intégrateurs)."
                        href="/p/api"
                        internal
                        accent="emerald"
                        testid="api-link-marketing"
                    />
                </div>
            </div>

            {/* Highlights grid */}
            <div>
                <div className="text-[11px] uppercase tracking-wider text-foreground/45 font-semibold mb-3">
                    Points forts techniques
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {HIGHLIGHTS.map((h) => (
                        <div key={h.title} className="rounded-xl border border-stone-200 bg-white p-4 hover:border-primary/30 transition-colors">
                            <div className="flex items-center gap-2.5 mb-2">
                                <h.icon size={16} className="text-primary" />
                                <h4 className="font-medium text-sm">{h.title}</h4>
                            </div>
                            <p className="text-[12px] text-foreground/65 leading-relaxed">{h.desc}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Snippet */}
            <div className="rounded-2xl border border-stone-200 bg-stone-900 text-stone-100 p-5 font-mono text-[12px] leading-relaxed">
                <div className="text-stone-400 text-[10px] uppercase tracking-wider mb-2">Exemple cURL · authentification</div>
                <pre className="whitespace-pre-wrap break-words"><code>{`# 1) Obtenir un access token Supabase
curl -X POST "${API_BASE}/api/auth/signup" \\
  -H "Content-Type: application/json" \\
  -d '{"email":"…","password":"…","full_name":"…"}'

# 2) Appeler un endpoint protégé avec le Bearer
curl "${API_BASE}/api/account-plans/me" \\
  -H "Authorization: Bearer <ACCESS_TOKEN>"`}</code></pre>
            </div>

            <p className="text-[11px] text-foreground/45 text-center">
                Pour ouvrir des accès partenaires limités, contactez l'équipe technique pour générer
                un token API dédié + scopes restreints (roadmap).
            </p>
        </div>
    );
}
